<template>
  <nav v-if="lastPage > 1" class="app-pager" aria-label="Pagination">
    <button
      type="button"
      class="app-pager__btn app-pager__btn--icon"
      :disabled="currentPage <= 1"
      aria-label="Previous page"
      @click="changeTo(currentPage - 1)"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>

    <button
      v-for="(item, idx) in pageItems"
      :key="`pg-${idx}-${item}`"
      type="button"
      class="app-pager__btn"
      :class="{ 'is-active': item === currentPage, 'is-ellipsis': item === '…' }"
      :disabled="item === '…' || item === currentPage"
      @click="item !== '…' && changeTo(item)"
    >
      {{ item }}
    </button>

    <button
      type="button"
      class="app-pager__btn app-pager__btn--icon"
      :disabled="currentPage >= lastPage"
      aria-label="Next page"
      @click="changeTo(currentPage + 1)"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>

    <div v-if="showJumpBox" class="app-pager__jump">
      <span class="app-pager__divider" aria-hidden="true"></span>
      <input
        v-model="jumpInput"
        type="text"
        inputmode="numeric"
        class="app-pager__jump-input"
        :placeholder="String(currentPage)"
        :aria-label="`Go to page, 1 to ${lastPage}`"
        @keydown.enter.prevent="submitJump"
        @blur="jumpInput = ''"
      >
      <button
        type="button"
        class="app-pager__btn app-pager__btn--icon app-pager__jump-go"
        aria-label="Go to page"
        @click="submitJump"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </div>
  </nav>
</template>

<script>
export default {
  name: 'AppPager',
  props: {
    currentPage: { type: Number, default: 1 },
    lastPage: { type: Number, default: 1 },
    siblingCount: { type: Number, default: 1 },
    // Jump-to-page box. Defaults on, but only renders once the list is long
    // enough that the numbered buttons start collapsing into ellipses.
    jump: { type: Boolean, default: true }
  },
  data () {
    return {
      jumpInput: ''
    }
  },
  computed: {
    showJumpBox () {
      const total = Math.max(1, Number(this.lastPage) || 1)
      const sibling = Math.max(0, Number(this.siblingCount) || 0)
      // Same slot count used by pageItems() to decide when ellipses appear.
      const totalSlots = sibling * 2 + 5
      return this.jump && total > totalSlots
    },
    pageItems () {
      const total = Math.max(1, Number(this.lastPage) || 1)
      const current = Math.max(1, Math.min(Number(this.currentPage) || 1, total))
      const sibling = Math.max(0, Number(this.siblingCount) || 0)

      const totalSlots = sibling * 2 + 5
      if (total <= totalSlots) {
        return Array.from({ length: total }, (_, i) => i + 1)
      }

      const leftSibling = Math.max(current - sibling, 2)
      const rightSibling = Math.min(current + sibling, total - 1)
      const showLeftEllipsis = leftSibling > 2
      const showRightEllipsis = rightSibling < total - 1

      const items = [1]
      if (showLeftEllipsis) items.push('…')
      for (let i = leftSibling; i <= rightSibling; i++) {
        items.push(i)
      }
      if (showRightEllipsis) items.push('…')
      items.push(total)
      return items
    }
  },
  methods: {
    changeTo (page) {
      const target = Number(page)
      if (!Number.isFinite(target)) return
      if (target < 1 || target > this.lastPage) return
      if (target === this.currentPage) return
      this.$emit('change', target)
    },
    submitJump () {
      const target = Math.floor(Number(this.jumpInput))
      this.jumpInput = ''
      if (!Number.isFinite(target)) return
      const clamped = Math.min(Math.max(1, target), Math.max(1, Number(this.lastPage) || 1))
      this.changeTo(clamped)
    }
  }
}
</script>

<style scoped>
.app-pager {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.3rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.app-pager__btn {
  min-width: 32px;
  height: 32px;
  padding: 0 0.55rem;
  border: none;
  outline: none;
  background: transparent;
  color: #475569;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1;
  border-radius: 999px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.app-pager__btn:hover:not(:disabled):not(.is-active):not(.is-ellipsis) {
  background: #f1f5f9;
  color: #1f2a44;
}

.app-pager__btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(14, 91, 70, 0.22);
}

.app-pager__btn:disabled {
  cursor: not-allowed;
}

.app-pager__btn--icon {
  min-width: 32px;
  width: 32px;
  padding: 0;
}

.app-pager__btn--icon:disabled {
  opacity: 0.4;
}

.app-pager__btn.is-active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 4px 10px rgba(10, 74, 58, 0.25);
  cursor: default;
  transform: translateY(-1px);
}

.app-pager__btn.is-ellipsis {
  cursor: default;
  color: #94a3b8;
  background: transparent;
}

.app-pager__jump {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding-left: 0.1rem;
}

.app-pager__divider {
  width: 1px;
  height: 18px;
  margin: 0 0.2rem;
  background: #e5e9f2;
}

.app-pager__jump-input {
  width: 42px;
  height: 28px;
  padding: 0 0.35rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  font-family: inherit;
  font-variant-numeric: tabular-nums;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.app-pager__jump-input::placeholder {
  color: #b6bdc9;
  font-weight: 500;
}

.app-pager__jump-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.app-pager__btn.app-pager__jump-go {
  color: #0e5b46;
}

.app-pager__btn.app-pager__jump-go:hover:not(:disabled) {
  background: #f1f5f9;
}
</style>
